import type { Dispatch, SetStateAction, UIEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import hot from '../../assets/casino/hot.svg'
import useDimensions from '../../hooks/useDimensions'
import { useBoundStore } from '../../store/store'
import type { CasinoCategory, Game } from '../../types/game'
import classNames from '../../utils/classNames'
import Icon from '../icon'
import GameCategoryIcon from './game-category-icon'

interface GameCategoriesProps {
  category: string
  setCategory: (category: CasinoCategory) => void
  favorites: Game[]
  setShowFilters: Dispatch<SetStateAction<boolean>>
  showFilters: boolean
}

const GameCategories = (props: GameCategoriesProps) => {
  const { t } = useTranslation('casino')
  const username = useBoundStore(state => state.user.username)
  const isFullScreen = useBoundStore(state => state.common.isFullScreen)
  const gameCategories = useBoundStore(state => state.casino.categories)
  const theme = useBoundStore(state => state.common.theme)
  const { category, setCategory, favorites, setShowFilters, showFilters } =
    props
  const { isDesktop } = useDimensions()

  const SLICED_TABS: CasinoCategory[] = useMemo(
    () =>
      isDesktop && username && favorites.length
        ? gameCategories
        : !isDesktop
        ? gameCategories.filter(
            c => c.id !== 'favorites' && c.id !== 'recent_played',
          )
        : gameCategories.filter(c => c.id !== 'favorites'),
    [isDesktop, username, favorites.length, gameCategories],
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollEnd, setScrollEnd] = useState(false)
  const [scrollStart, setScrollStart] = useState(true)

  const onScroll = (e: UIEvent) => {
    const element = e.target as HTMLDivElement
    setScrollEnd(
      element.scrollWidth - element.scrollLeft === element.clientWidth,
    )
    setScrollStart(element.scrollLeft < 50)
  }

  useEffect(() => {
    if (isDesktop) return
    const index = SLICED_TABS.findIndex(item => item.id === category)
    scrollRef.current?.scrollTo({
      top: 0,
      left: index * 50,
      behavior: 'smooth',
    })
  }, [SLICED_TABS, category])

  return (
    <div
      className={classNames(
        'sticky z-10 left-0',
        isFullScreen ? 'top-0' : 'top-[60px]',
        'xl:relative xl:top-[unset]',
      )}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="w-full gap-2.5 flex items-center justify-start flex-nowrap overflow-x-auto bg-primary mb-2.5 px-1.5 py-0 xl:w-[900px] xl:mb-0 xl:pl-0 xl:pr-6 xl:py-0 no-scrollbar">
        {!isDesktop && (
          <>
            <div
              className={classNames(
                showFilters
                  ? 'text-highlight xl:before:opacity-0 [&>svg]:fill-highlight'
                  : 'text-secondary-txt [&>svg]:fill-secondary-txt',
                'v-flx-ctr min-w-[59px] relative cursor-pointer px-0 py-2 xl:px-0 xl:py-3 xl:hover:opacity-0 xl:min-w-[73px]',
              )}
              onClick={() => {
                setShowFilters(prev => !prev)
              }}>
              <div className="w-[35px] h-[35px] mb-0.5">
                <Icon
                  className="transition-all duration-[0.2s] ease-[ease-in-out] w-[35px] h-[35px]"
                  id="search"
                  width={16}
                  height={16}></Icon>
              </div>
              <p
                className={classNames(
                  showFilters
                    ? 'text-highlight before:opacity-100'
                    : 'text-secondary-txt',
                  'text-xs leading-normal text-center uppercase whitespace-nowrap relative xl:text-sm',
                  `before:content-[''] before:w-full before:h-0.5 before:bg-highlight before:opacity-0 before:transition-all`,
                  'before:duration-[0.2s] before:ease-[ease-in-out] before:absolute before:rounded-[1px] before:left-0 before:bottom-0',
                )}>
                {t('search-category')}
              </p>
            </div>
            <div className="min-w-[1.5px] h-[31px] bg-[color:var(--secondary-txt-50)] mb-[7px] rounded-[1.5px]" />
          </>
        )}
        {!SLICED_TABS.length
          ? Array.from({ length: 17 }, (_, index) => (
              <div
                key={index}
                className="v-flx-ctr animate-pulse min-w-[50px] px-0 py-2 xl:px-0 xl:py-3 xl:min-w-[73px]">
                <div className="bg-secondary block w-10 aspect-square rounded-[5px]" />
                <p className="w-10 h-[11px] bg-secondary mt-[11px] rounded-[3px]" />
              </div>
            ))
          : SLICED_TABS.map((item, index) => (
              <div
                className={classNames(
                  category === item.id
                    ? 'text-highlight xl:before:opacity-0 [&>svg]:fill-highlight'
                    : 'text-secondary-txt [&>svg]:fill-secondary-txt',
                  'v-flx-ctr min-w-[59px] relative cursor-pointer text-secondary-txt px-0 py-2 xl:px-0 xl:py-3 xl:min-w-[73px]',
                )}
                key={index}
                onClick={() => {
                  setCategory(item)
                }}>
                <GameCategoryIcon
                  {...item}
                  isActive={category === item.id}
                  theme={theme}
                />
                <p
                  className={classNames(
                    category === item.id
                      ? 'text-highlight before:opacity-100'
                      : 'text-secondary-txt',
                    'text-xs leading-normal text-center uppercase whitespace-nowrap relative xl:text-sm',
                    `before:content-[''] before:w-full before:h-0.5 before:bg-highlight before:opacity-0 before:transition-all`,
                    'before:duration-[0.2s] before:ease-[ease-in-out] before:absolute before:rounded-[1px] before:left-0 before:bottom-0',
                  )}>
                  {item.category}
                </p>
                <div
                  className={classNames(
                    category === item.id ? 'opacity-100' : 'opacity-0',
                    'absolute font-bold text-[10px] text-white bg-highlight z-[1]',
                    'transition-all duration-[0.2s] ease-[ease-in-out] px-[5px] py-px rounded-[3px] -right-2.5 top-[5px]',
                  )}>
                  {item.id === 'favorites' ? favorites.length : item.count}
                </div>
                {item.id === '15656' && (
                  <img
                    className={classNames(
                      category === item.id ? 'opacity-0' : 'opacity-100',
                      'w-[25px] absolute -right-2.5 top-0',
                    )}
                    src={hot}
                    alt="hot"
                  />
                )}
              </div>
            ))}
      </div>
      <div
        className={classNames(
          scrollStart ? 'opacity-0' : 'opacity-100',
          'absolute h-full w-[70px] pointer-events-none transition-all duration-[0.2s] ease-[ease-in-out]',
          'right-0 top-0 flx-ctr rotate-0 pl-0 pr-[30px] left-0 animate-arrowAnim',
        )}>
        <Icon
          id="triangle-left"
          width="9"
          height="16"
          fill="var(--highlight)"
        />
      </div>
      <div
        className={classNames(
          scrollEnd ? 'opacity-0' : 'opacity-100',
          'flx-ctr absolute h-full w-[70px] pointer-events-none transition-all duration-[0.2s] ease-[ease-in-out] pl-[30px] right-0 top-0 animate-arrowAnim',
        )}>
        <Icon
          id="triangle-right"
          width="9"
          height="16"
          fill="var(--highlight)"
        />
      </div>
    </div>
  )
}

export default GameCategories
